import { useState } from "react";
import { Link } from "react-router-dom";
import { UsersRound, Plus, Ship, Hotel, MapPin, CalendarDays, LinkIcon } from "lucide-react";
import { useDemo } from "@/store/DemoStore";
import { Card, Empty, PageHeader, StatusBadge, btn } from "@/components/common";
import { NewGroupDialog } from "@/components/NewGroupDialog";
import { fmtDate } from "@/lib/trip";

function GroupCard({ group, showAgent }) {
  const TypeIcon = group.type === "Resort" ? Hotel : Ship;
  const dates = [group.departure, group.returnDate].filter(Boolean).map(fmtDate).join(" – ");
  return (
    <Link to={`/agent/groups/${group.id}`} data-testid={`group-card-${group.code}`} className="block">
      <Card className="overflow-hidden transition-shadow hover:shadow-md">
        <div className="relative h-32 w-full bg-navy-900">
          {group.cover
            ? <img src={group.cover} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover" onLoad={(e) => { e.currentTarget.style.display = ""; }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
            : <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-navy-800 to-navy-950"><TypeIcon className="h-7 w-7 text-white/30" /></div>}
          <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-0.5 text-[11px] font-semibold text-navy-800 shadow-sm backdrop-blur">
            <TypeIcon className="h-3.5 w-3.5" />{group.type}
          </span>
          {group.published
            ? <StatusBadge status="BOOKED" className="absolute right-3 top-3" />
            : <span className="absolute right-3 top-3 rounded-full bg-slate-900/70 px-2.5 py-0.5 text-[11px] font-semibold text-white backdrop-blur">Draft</span>}
        </div>
        <div className="space-y-2.5 p-4">
          <h3 className="font-display text-base font-bold leading-tight text-navy-900">{group.name}</h3>
          <div className="space-y-1.5 text-sm text-slate-600">
            {group.destination && <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-slate-400" />{group.destination}</p>}
            {(group.supplier || group.vessel) && <p className="flex items-center gap-2"><TypeIcon className="h-3.5 w-3.5 text-slate-400" />{[group.supplier, group.vessel].filter(Boolean).join(" · ")}</p>}
            {dates && <p className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5 text-slate-400" />{dates}</p>}
          </div>
          <div className="flex items-center gap-2 border-t border-slate-100 pt-2.5 text-xs text-slate-500">
            <LinkIcon className="h-3.5 w-3.5 text-slate-400" />
            <code className="truncate font-mono text-navy-700">/g/{group.code}</code>
          </div>
          {showAgent && group.agentName && <p className="text-xs text-slate-400">Created by {group.agentName}</p>}
        </div>
      </Card>
    </Link>
  );
}

export default function GroupsPage() {
  const { groups, me } = useDemo();
  const [tab, setTab] = useState("mine");
  const [open, setOpen] = useState(false);

  const mine = groups.filter((g) => g.agentId === me?.id);
  const list = tab === "mine" ? mine : groups;

  return (
    <div data-testid="agent-groups-page">
      <PageHeader
        title="Groups"
        subtitle="Create group trips and publish a public page clients can browse"
        actions={<button onClick={() => setOpen(true)} className={btn.primary} data-testid="create-group-btn"><Plus className="h-4 w-4" />Create Group</button>}
      />

      <div className="mb-6 inline-flex rounded-xl bg-slate-100 p-1">
        <button onClick={() => setTab("mine")} data-testid="groups-tab-mine"
          className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${tab === "mine" ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900"}`}>
          My Groups <span className="ml-1 text-xs text-slate-400">{mine.length}</span>
        </button>
        <button onClick={() => setTab("all")} data-testid="groups-tab-all"
          className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${tab === "all" ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900"}`}>
          All Groups <span className="ml-1 text-xs text-slate-400">{groups.length}</span>
        </button>
      </div>

      {list.length
        ? <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-testid="groups-grid">
            {list.map((g) => <GroupCard key={g.id} group={g} showAgent={tab === "all"} />)}
          </div>
        : <Empty icon={UsersRound}
            title={tab === "mine" ? "No groups yet" : "No groups created"}
            text={tab === "mine" ? "Create your first group to generate a public page clients can view." : "Groups created by any advisor will appear here."} />}

      {open && <NewGroupDialog open onOpenChange={setOpen} />}
    </div>
  );
}
